import { makeAuthenticatedRequest } from './authService';
import { logApiConfig } from '../config/api';

if (process.env.NODE_ENV === 'development') {
  logApiConfig();
}

export interface Period {
  id: number;
  user_id: number;
  start_date: string;
  end_date: string | null;
  cycle_length: number | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

export interface CreatePeriodData {
  user_id: number;
  start_date: string;
  end_date?: string;
  notes?: string;
}

export interface UpdatePeriodData {
  start_date?: string;
  end_date?: string;
  notes?: string;
}

export interface PeriodFilters {
  start_date?: string;
  end_date?: string;
  limit?: number;
  offset?: number;
}

export interface PeriodPrediction {
  predicted_start_date: string;
  cycle_number: number;
}

export interface PeriodStats {
  total_periods: number;
  average_cycle_length: number | null;
  average_period_duration: number | null;
  last_period_start: string | null;
  last_period_end: string | null;
  next_predicted_period: string | null;
}

class PeriodCalendarService {
  async getPeriods(userId: number, filters?: PeriodFilters): Promise<{ success: boolean; periods?: Period[]; count?: number; message?: string }> {
    try {
      const queryParams = new URLSearchParams();
      if (filters?.start_date) queryParams.append('start_date', filters.start_date);
      if (filters?.end_date) queryParams.append('end_date', filters.end_date);
      if (filters?.limit) queryParams.append('limit', filters.limit.toString());
      if (filters?.offset) queryParams.append('offset', filters.offset.toString());
      
      const queryString = queryParams.toString();
      const url = `/period-calendar/user/${userId}${queryString ? `?${queryString}` : ''}`;
      
      const response = await makeAuthenticatedRequest(url, {
        method: 'GET'
      });
      
      return response;
    } catch (error) {
      console.error('Error fetching periods:', error);
      throw error;
    }
  }

  async getPeriodById(id: number): Promise<{ success: boolean; period?: Period; message?: string }> {
    try {
      const response = await makeAuthenticatedRequest(`/period-calendar/${id}`, {
        method: 'GET'
      });
      
      return response;
    } catch (error) {
      console.error('Error fetching period:', error);
      throw error;
    }
  }

  async createPeriod(data: CreatePeriodData): Promise<{ success: boolean; id?: number; cycle_length?: number | null; message?: string }> {
    try {
      const response = await makeAuthenticatedRequest('/period-calendar', {
        method: 'POST',
        body: JSON.stringify(data)
      });
      
      return response;
    } catch (error) {
      console.error('Error creating period:', error);
      throw error;
    }
  }

  async updatePeriod(id: number, data: UpdatePeriodData): Promise<{ success: boolean; message?: string }> {
    try {
      const response = await makeAuthenticatedRequest(`/period-calendar/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data)
      });
      
      return response;
    } catch (error) {
      console.error('Error updating period:', error);
      throw error;
    }
  }

  async deletePeriod(id: number): Promise<{ success: boolean; message?: string }> {
    try {
      const response = await makeAuthenticatedRequest(`/period-calendar/${id}`, {
        method: 'DELETE'
      });
      
      return response;
    } catch (error) {
      console.error('Error deleting period:', error);
      throw error;
    }
  }

  async getPredictions(userId: number, count: number = 6): Promise<{ success: boolean; average_cycle_length?: number; last_period_start?: string; predictions?: PeriodPrediction[]; message?: string }> {
    try {
      const response = await makeAuthenticatedRequest(`/period-calendar/user/${userId}/predictions?count=${count}`, {
        method: 'GET'
      });
      
      return response;
    } catch (error) {
      console.error('Error fetching predictions:', error);
      throw error;
    }
  }

  async getStats(userId: number): Promise<{ success: boolean; stats?: PeriodStats; message?: string }> {
    try {
      const response = await makeAuthenticatedRequest(`/period-calendar/user/${userId}/stats`, {
        method: 'GET'
      });
      
      return response;
    } catch (error) {
      console.error('Error fetching stats:', error);
      throw error;
    }
  }
}

export const periodCalendarService = new PeriodCalendarService();

